import type React from 'react';
import { useEffect, useState } from 'react';
import { listUserRoles, assignUserRole, revokeUserRole, type AdminUserRole } from '../../lib/adminApi';
import { useRole } from '../../lib/roleContext';
import { AdminPageShell, cls } from './shared';

export function UsersPage() {
  const { role: myRole } = useRole();
  const [users, setUsers] = useState<AdminUserRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newIdentifier, setNewIdentifier] = useState('');
  const [newRole, setNewRole] = useState<'admin' | 'design'>('design');
  const [saving, setSaving] = useState(false);

  function load() {
    listUserRoles()
      .then((r) => {
        setUsers(r.users);
        setLoading(false);
      })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : 'Failed to load');
        setLoading(false);
      });
  }

  useEffect(load, []);

  async function handleAssign() {
    if (!newIdentifier.trim()) return;
    setSaving(true);
    try {
      await assignUserRole({ identifier: newIdentifier.trim(), role: newRole });
      setNewIdentifier('');
      load();
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Error assigning role');
    } finally {
      setSaving(false);
    }
  }

  async function handleRevoke(identifier: string) {
    if (!confirm('Revoke this user\'s elevated role?')) return;
    try {
      await revokeUserRole(identifier);
      load();
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Error revoking role');
    }
  }

  const roleBadge = (role: AdminUserRole['role']) =>
    role === 'admin'
      ? 'bg-vari-navy/10 text-vari-navy'
      : role === 'design'
        ? 'bg-vari-yellow/30 text-vari-navy'
        : 'bg-gray-100 text-vari-gray';

  if (loading) return <AdminPageShell title="User Roles"><p className="text-vari-gray text-sm">Loading…</p></AdminPageShell>;
  if (error) return <AdminPageShell title="User Roles"><p className="text-red-600 text-sm">{error}</p></AdminPageShell>;

  return (
    <AdminPageShell title="User Roles">
      <div className={cls.formPanel}>
        <p className="text-sm font-medium text-vari-navy mb-3">Assign Role</p>
        <div className="flex items-end gap-3">
          <div className="flex-1">
            <label className="text-xs text-vari-gray block mb-1">Vari email or Entra OID</label>
            <input
              className={cls.input}
              placeholder="name@vari.com or xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
              value={newIdentifier}
              onChange={(e) => setNewIdentifier(e.target.value)}
            />
          </div>
          <div>
            <label className="text-xs text-vari-gray block mb-1">Role</label>
            <select
              className={cls.select}
              value={newRole}
              onChange={(e) => setNewRole(e.target.value as 'admin' | 'design')}
            >
              <option value="design">design</option>
              <option value="admin">admin</option>
            </select>
          </div>
          <button
            onClick={() => void handleAssign()}
            disabled={saving || !newIdentifier.trim()}
            className={cls.btnSmPrimary}
          >
            {saving ? 'Saving…' : 'Assign'}
          </button>
        </div>
        <p className="text-xs text-vari-gray mt-3">
          Enter a <strong>@vari.com email</strong> to assign by email (works before first sign-in), or an{' '}
          <strong>Entra OID</strong> for precision. Find an OID at{' '}
          <a href="https://jwt.ms" target="_blank" rel="noopener noreferrer" className="underline">
            jwt.ms
          </a>{' '}
          after signing in.
        </p>
      </div>

      <div className={cls.tableWrap}>
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-vari-soft-gray">
            <tr>
              <th className={cls.th}>User</th>
              <th className={cls.th}>Role</th>
              <th className={cls.th}>Last seen</th>
              <th className={cls.th}>Assigned by</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {users.map((u, i) => {
              const primary = u.name ?? u.email ?? u.oid ?? u.roleIdentifier ?? '—';
              const secondary = u.email && u.email !== primary ? u.email : u.oid ?? u.roleIdentifier;
              return (
                <tr key={u.oid ?? u.roleIdentifier ?? i} className={cls.tr(i % 2 === 1)}>
                  <td className={cls.td}>
                    <div className="font-medium text-vari-navy flex items-center gap-2">
                      {primary}
                      {!u.signedIn && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 text-vari-gray font-medium">
                          invited
                        </span>
                      )}
                    </div>
                    {secondary && secondary !== primary && (
                      <div className="text-xs text-vari-gray font-mono truncate max-w-xs">{secondary}</div>
                    )}
                  </td>
                  <td className={cls.td}>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${roleBadge(u.role)}`}>
                      {u.role}
                    </span>
                  </td>
                  <td className={`${cls.td} text-vari-gray`}>
                    {u.lastSeen ? new Date(u.lastSeen).toLocaleDateString() : '—'}
                  </td>
                  {(() => {
                    const resolved = u.assignedByName ?? u.assignedByEmail;
                    const display = resolved ?? u.assignedBy ?? '—';
                    const isRaw = !resolved && !!u.assignedBy && u.assignedBy !== 'bootstrap';
                    return (
                      <td className={`${cls.td} text-vari-gray ${isRaw ? 'font-mono text-xs' : ''}`}>
                        {display}
                      </td>
                    );
                  })()}
                  <td className={cls.tdRight}>
                    {myRole === 'admin' && u.role !== 'user' && u.roleIdentifier && (
                      <button
                        onClick={() => void handleRevoke(u.roleIdentifier!)}
                        className={cls.btnDanger}
                      >
                        Revoke
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            {users.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-vari-gray text-sm">
                  No users yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </AdminPageShell>
  );
}
